import { useContext, useEffect, useState } from "react";
import { Context } from "../../../context/Context";
import { NoticeBox } from "../../ui";
import { ActionDialog, DialogFooter, bothNames } from "../topActions/shared";
import { approveAiImportAPI, fetchAiImportPlanAPI } from "../../../utils/api/menuAiImport";
import { aiImportText } from "./aiImportText";
import { menuErrorMessage } from "../menuRole";
import { aiImportErrorMessage } from "./aiImportModel";

function PlanGroup({ title, tone, children, count }) {
  return (
    <section className={`ai-import-plan-group is-${tone}`}>
      <h4>
        {title} <span className="ai-import-plan-count">{count}</span>
      </h4>
      {children}
    </section>
  );
}

const names = (list, limit = 30) => (
  <ul className="ai-import-plan-list">
    {list.slice(0, limit).map((entry) => (
      <li key={entry.key || entry.itemId} dir="auto">
        {bothNames(entry.name || entry.title)}
        {entry.extra ? <span className="menu-secondary"> · {entry.extra}</span> : null}
      </li>
    ))}
    {list.length > limit && <li className="menu-secondary">+{list.length - limit}</li>}
  </ul>
);

// Created / Changed / Reused / Left unchanged, computed by the backend with
// the same code the approval runs. Approval sends the revision the reviewer
// saw, so a draft changed meanwhile is refused instead of approved blindly.
export default function AiImportPlanDialog({ authorize, draftId, onClose, onApproved, onBlocked }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const [state, setState] = useState({ loading: true, plan: null, revision: null, error: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchAiImportPlanAPI(draftId)
      .then((res) => alive && setState({ loading: false, plan: res.plan, revision: res.revision, error: "" }))
      .catch((err) => alive && setState({ loading: false, plan: null, revision: null, error: aiImportErrorMessage(err, t, lang) }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const approve = async () => {
    // Applying a draft changes the live menu: re-checked for the draft's restaurant (NA-010).
    if (!authorize?.()) return setState((prev) => ({ ...prev, error: menuErrorMessage({ status: 403 }, lang) }));
    setBusy(true);
    try {
      const res = await approveAiImportAPI(draftId, state.revision);
      onApproved(res);
    } catch (err) {
      setBusy(false);
      if (err.code === "APPROVAL_BLOCKED") onBlocked(err.details?.problems || []);
      setState((prev) => ({ ...prev, error: aiImportErrorMessage(err, t, lang) }));
    }
  };

  const { plan } = state;
  const blocked = Boolean(plan?.blocking?.length);
  return (
    <ActionDialog
      title={t.planTitle}
      busy={busy}
      error={state.error}
      onClose={onClose}
      footer={<DialogFooter submitLabel={busy ? t.approving : t.approve} tone="success" onSubmit={approve} onCancel={onClose} busy={busy} disabled={!plan || blocked} />}
    >
      {state.loading && <p className="menu-note">{t.loadingPlan}</p>}
      {plan && (
        <>
          <p className="ai-import-plan-target">{plan.mode === "new_menu" ? t.planMenuNew(plan.menuName) : t.planMenuExisting(plan.menuName)}</p>
          {blocked && (
            <NoticeBox variant="danger" size="sm" title={t.blockingTitle}>
              <ul className="ai-import-plan-list">
                {plan.blocking.map((problem, i) => (
                  <li key={`${problem.code}-${i}`}>{problem.message}</li>
                ))}
              </ul>
            </NoticeBox>
          )}
          <div className="ai-import-plan">
            <PlanGroup title={t.created} tone="created" count={plan.counts.created.items + plan.counts.created.sections + plan.counts.created.additions}>
              <p className="menu-secondary">
                {t.planCounts.sections(plan.counts.created.sections)} · {t.planCounts.items(plan.counts.created.items)} · {t.planCounts.additions(plan.counts.created.additions)}
              </p>
              {names(plan.items.create)}
              {plan.additions.create.length > 0 && names(plan.additions.create)}
            </PlanGroup>
            {plan.mode !== "new_menu" && (
              <PlanGroup title={t.changed} tone="changed" count={plan.counts.changed.items}>
                {names(plan.items.update.map((u) => ({ ...u, name: u.currentName, extra: u.changed.map((f) => t.changedFields[f] || f).join(", ") })))}
              </PlanGroup>
            )}
            <PlanGroup title={t.reused} tone="reused" count={plan.counts.reused.items + plan.counts.reused.additions + plan.counts.reused.sections}>
              {names([...plan.items.reuse, ...plan.additions.reuse, ...plan.sections.reuse])}
            </PlanGroup>
            {plan.mode !== "new_menu" && (
              <PlanGroup title={t.unchanged} tone="unchanged" count={plan.counts.unchanged.items}>
                <p className="menu-secondary">{t.planCounts.items(plan.counts.unchanged.items)}</p>
              </PlanGroup>
            )}
            {plan.counts.excluded.items > 0 && (
              <PlanGroup title={t.excludedCount} tone="excluded" count={plan.counts.excluded.items}>
                {names(plan.items.excluded)}
              </PlanGroup>
            )}
          </div>
          {(plan.omittedSuggestions.calories > 0 || plan.omittedSuggestions.allergens > 0) && (
            <NoticeBox variant="warning" size="sm">
              {t.omitted(plan.omittedSuggestions.calories, plan.omittedSuggestions.allergens)}
            </NoticeBox>
          )}
          {plan.aiTextFields > 0 && <NoticeBox variant="info" size="sm">{t.aiText(plan.aiTextFields)}</NoticeBox>}
          {plan.skippedRelationships > 0 && <p className="menu-note">{t.skippedLinks(plan.skippedRelationships)}</p>}
        </>
      )}
    </ActionDialog>
  );
}
